import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { formService } from '../services/formService';
import { useFormData } from '../hooks/useFormData';
import { useTracking } from '../hooks/useTracking';
import PlatypusForm from '../components/shared/PlatypusForm';

const PopupForm = ({ isVisible, onClose }) => {
    const initialFormState = {
        name: '',
        email: '',
        phone: '',
        city: '',
        dogCount: '',
        catCount: '',
    };

    for (let i = 0; i < 20; i++) {
        initialFormState[`dogName_${i}`] = '';
        initialFormState[`dogAge_${i}`] = '';
        initialFormState[`catName_${i}`] = '';
        initialFormState[`catAge_${i}`] = '';
    }

    const { formData, handleChange, resetForm } = useFormData(initialFormState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
    const { trackButton } = useTracking();

    const validateEmail = (email) => {
        if (!email || !email.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return 'Please enter a valid email address';
        const domain = email.toLowerCase().split('@')[1];
        const blockedDomainPatterns = [
            'localhost', 'temp-mail.org', 'guerrillamail.com', 'mailinator.com', 'yopmail.com',
            'tempmail.net', 'throwaway.email', 'getnada.com', 'trashmail.com', 'example.',
            'demo.', 'sample.', 'fake.', 'invalid.', 'null.', 'temporary.', 'disposable.',
            'burner.', 'phantom.', 'ghost.', 'void.', 'nowhere.'
        ];
        const exactBlocked = blockedDomainPatterns.filter(p => !p.endsWith('.')).some(b => domain === b);
        const patternBlocked = blockedDomainPatterns.filter(p => p.endsWith('.')).some(p => domain.startsWith(p));
        if (exactBlocked || patternBlocked) return 'Please use a valid business or personal email address';
        return null;
    };

    const validateName = (name) => {
        if (!name || name.trim().length < 2) return 'Please enter your full name';
        if (!/^[a-zA-Z\s]+$/.test(name.trim())) return 'Name should only contain letters and spaces';
        return null;
    };

    const validatePhone = (phone) => {
        if (!phone || phone.trim().length < 7) return 'Please enter a valid phone number';
        if (!/^[\d\s\-\+\(\)]+$/.test(phone.trim())) return 'Please enter a valid phone number';
        return null;
    };

    const validateCity = (city) => {
        if (!city || city.trim().length < 2) return 'Please enter your city';
        if (!/^[a-zA-Z\s]+$/.test(city.trim())) return 'City name should only contain letters and spaces';
        return null;
    };

    const validatePetInputs = (count, type) => {
        const errors = {};
        for (let i = 0; i < count; i++) {
            const nameKey = `${type}Name_${i}`;
            const ageKey = `${type}Age_${i}`;
            const name = formData[nameKey];
            const age = formData[ageKey];
            if (name && !/^[a-zA-Z\s]+$/.test(name.trim())) {
                errors[nameKey] = `${type} ${i + 1} name should only contain letters`;
            }
            if (age && (isNaN(age) || age < 0 || age > 50)) {
                errors[ageKey] = `${type} ${i + 1} Please enter age`;
            }
        }
        return errors;
    };

    const checkRateLimit = () => {
        const now = Date.now();
        const timeSinceLast = now - lastSubmissionTime;
        const fifteenMins = 15 * 60 * 1000;
        if (timeSinceLast < fifteenMins) {
            const minsLeft = Math.ceil((fifteenMins - timeSinceLast) / 60000);
            return `Please wait ${minsLeft} more minutes before submitting again`;
        }
        return null;
    };


    //cat or dog data in json formate
    const extractPetData = (formData) => {
        const dogs = [];
        const cats = [];

        for (let i = 0; i < 20; i++) {
            const dogName = formData[`dogName_${i}`]?.trim();
            const dogAge = formData[`dogAge_${i}`]?.trim();
            if (dogName || dogAge) {
                dogs.push({ name: dogName || '', age: dogAge || '' });
            }

            const catName = formData[`catName_${i}`]?.trim();
            const catAge = formData[`catAge_${i}`]?.trim();
            if (catName || catAge) {
                cats.push({ name: catName || '', age: catAge || '' });
            }
        }

        return { dogs, cats };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { name, email, phone, city } = formData;

        const rateLimitError = checkRateLimit();
        if (rateLimitError) {
            toast.error(rateLimitError);
            return;
        }

        const dogCount = Array.from({ length: 20 }).filter((_, i) =>
            formData[`dogName_${i}`]?.trim() || formData[`dogAge_${i}`]?.trim()
        ).length;

        const catCount = Array.from({ length: 20 }).filter((_, i) =>
            formData[`catName_${i}`]?.trim() || formData[`catAge_${i}`]?.trim()
        ).length;

        const errors = {
            name: validateName(name),
            email: validateEmail(email),
            phone: validatePhone(phone),
            city: validateCity(city),
            ...validatePetInputs(dogCount, 'dog'),
            ...validatePetInputs(catCount, 'cat')
        };

        setFieldErrors(errors);
        if (Object.values(errors).some(Boolean)) {
            toast.error('❌ Please fix the errors above');
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const petData = extractPetData(formData); // <-- Get dogs and cats JSON

            const finalData = {
                name,
                email,
                phone,
                city,
                dogCount,
                catCount,
                ...petData // { dogs: [...], cats: [...] }
            };

            //console.log(finalData);

            await formService.submitHeroForm(finalData);
            toast.success('Form submitted successfully!');
            resetForm();
            setFieldErrors({});
            setLastSubmissionTime(Date.now());
            onClose();
        } catch (err) {
            toast.error(err?.error || 'Submission failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleButtonClick = () => {
        trackButton('popup_submit', {
            formSection: 'popup',
            formData: {
                hasName: !!formData.name,
                hasEmail: !!formData.email,
                hasPhone: !!formData.phone,
                hasCity: !!formData.city,
            },
        });
    };

    useEffect(() => {
        if (isVisible) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-md z-50 px-4">
            <input type="text" name="website" style={{ position: 'absolute', left: '-9999px' }} tabIndex="-1" autoComplete="off" />

            <div className="custom-scroll bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto shadow-xl p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 px-3 py-2 text-white text-3xl leading-none"
                >
                    ×
                </button>

                <h2 className="text-lg md:text-xl font-medium text-white mb-6 mt-2 pr-10">
                    <span className="text-yellow-400 font-semibold">Start Your Journey</span> to Better Pet Care - With Trust, Technology & Love
                </h2>

                {Object.values(fieldErrors).some(error => error) && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-400 rounded-md">
                        <p className="text-red-400 text-sm">Please fix the following errors:</p>
                        <ul className="text-red-400 text-sm mt-1 list-disc list-inside">
                            {Object.entries(fieldErrors).map(([field, error]) => error && <li key={field}>{error}</li>)}
                        </ul>
                    </div>
                )}

                <PlatypusForm
                    formData={formData}
                    onFieldChange={handleChange}
                    onSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    fields={['name', 'email', 'phone', 'city']}
                    submitText={isSubmitting ? 'Submitting...' : 'Submit'}
                    layout="vertical"
                    className="mt-6"
                    fieldErrors={fieldErrors}
                    disabled={isSubmitting || Object.values(fieldErrors).some(Boolean)}
                    onClick={handleButtonClick}
                />
            </div>
        </div>
    );
};

export default PopupForm;
